import { Stack, Typography, Chip, IconButton, Breadcrumbs } from '@mui/joy';
import { Plus, TrashSimple, TreeView, X } from '@phosphor-icons/react';
import { useState } from 'react';
import {
  useFieldArray,
  FormProvider,
  UseFieldArrayAppend,
  useFormContext,
  Controller,
  UseFieldArrayUpdate,
  UseFieldArrayRemove,
  UseFormReturn,
} from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useNavigate,
  json,
  LoaderFunctionArgs,
  useLoaderData,
} from 'react-router-dom';

import { DeleteTemplateWarningDialog } from '../route';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogFooter,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Input, InputField } from '~/components/ui/input';
import { Option, SelectField } from '~/components/ui/select';
import { useTableBuilder } from '~/components/ui/table-builder';
import { TextArea, TextAreaField } from '~/components/ui/textarea';
import { PriorityChip, StatusChip } from '~/features/process-management/Common';
import {
  getPriorityName,
  PriorityValues,
  TaskStatus,
} from '~/features/process-management/configs';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { ProcessService } from '~/services/api/processes/endpoints';
import {
  CreateUpdateTemplate,
  SubTask,
  Task,
  TemplateSchema,
  formSchema,
  newSubTaskSchema,
  newTaskSchema,
  taskSchema,
  taskWithChildrenSchema,
} from '~/services/api/processes/types';
import { useWrappedMutation } from '~/services/api/rest-api';
import { useForm } from '~/utils/forms';
import { store } from '~/utils/store';
import { AtLeastOne } from '~/utils/type-helpers';

function PrioritySelectChoices() {
  return (
    <>
      <Option value={null} disabled>
        <FormattedMessage defaultMessage='Set priority' />
      </Option>
      <Option value={0}>
        <PriorityChip priority={PriorityValues.LOW} />
      </Option>
      <Option value={1}>
        <PriorityChip priority={PriorityValues.MEDIUM} />
      </Option>
      <Option value={2}>
        <PriorityChip priority={PriorityValues.HIGH} />
      </Option>
      <Option value={3}>
        <PriorityChip priority={PriorityValues.CRITICAL} />
      </Option>
    </>
  );
}

function NewTaskDialog({
  onCreate,
}: {
  onCreate: UseFieldArrayAppend<TemplateSchema, 'subtasks'>;
}) {
  const [open, setOpen] = useState(false); 
  const templateForm = useFormContext();
  const createTask = useWrappedMutation(
    ProcessService.endpoints.createTask.useMutation(),
  );
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const templateID = templateForm.watch('id');
  const form = useForm({
    schema: newTaskSchema,
    defaultValues: {
      name: '',
      description: '',
      template: templateID,
    },
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    await createTask.mutateAsync(data, {
      onSuccess: (newTask) => {
        form.reset();
        createToastNotification(
          intl.formatMessage({ defaultMessage: 'Task added.' }),
          'success',
        );
        onCreate(newTask);
        setOpen(false);
      },
      onError: () => {
        createToastNotification(
          intl.formatMessage({
            defaultMessage:
              'An error occurred while processing your request. Please try again later.',
          }),
          'error',
        );
      },
    });
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant='soft'
          color='neutral'
          sx={{
            textTransform: 'none',
          }}
          startDecorator={<Plus />}
          type='button'
        >
          <FormattedMessage defaultMessage='Add task' />
        </Button>
      </DialogTrigger>
      <DialogContent size='2xl' className='space-y-4'>
        <div className='flex flex-row justify-between items-center'>
          <DialogTitle>
            <FormattedMessage defaultMessage='New Task' />
          </DialogTitle>
          <DialogClose asChild>
            <IconButton size='lg'>
              <X size={20} />
            </IconButton>
          </DialogClose>
        </div>
        <form onSubmit={handleSubmit} className='space-y-3'>
          <InputField
            control={form.control}
            field='name'
            variant='plain'
            placeholder={intl.formatMessage({
              defaultMessage: 'Task Name',
            })}
          />
          <TextAreaField
            control={form.control}
            field='description'
            variant='plain'
            placeholder={intl.formatMessage({
              defaultMessage: 'Add description..',
            })}
            minRows={4}
          />
          <div className='flex flex-row gap-2 items-baseline'>
            <StatusChip status={TaskStatus.TODO} />
            <SelectField
              control={form.control}
              field='priority'
              mountOptionsToRoot={true}
            >
              <PrioritySelectChoices />
            </SelectField>
          </div>
          <DialogFooter>
            <Button
              variant='solid'
              color='secondary'
              type='submit'
              disabled={form.formState.isSubmitting}
            >
              <FormattedMessage defaultMessage='Create task' />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const DeleteWarningDialog = ({
  taskID,
  onSuccessfulDelete,
}: {
  taskID: number;
  onSuccessfulDelete: () => void;
}) => {
  const { createToastNotification } = useToastNotificationContext();
  const deleteTask = useWrappedMutation(
    ProcessService.endpoints.deleteTask.useMutation(),
  );

  const handleDelete = async () => {
    await deleteTask.mutateAsync(
      { id: taskID },
      {
        onSuccess: () => {
          createToastNotification('Task deleted.', 'success');
          onSuccessfulDelete();
        },
        onError: () => {
          createToastNotification(
            'An error occurred while processing your request. Please try again later.',
            'error',
          );
        },
      },
    );
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant='plain'
          color='danger'
          disabled={deleteTask.state.isLoading}
          startDecorator={<TrashSimple />}
        >
          <FormattedMessage defaultMessage='Delete task' />
        </Button>
      </DialogTrigger>
      <DialogContent className='space-y-4' size='3xl'>
        <DialogTitle>
          <FormattedMessage defaultMessage='Delete Task' />
        </DialogTitle>
        <Typography>
          <FormattedMessage
            defaultMessage="Would you like to delete this task? This action can't be undone.
          All subtasks under this task will also be removed."
          />
        </Typography>
        <DialogFooter>
          <DialogClose asChild>
            <Button
              variant='outlined'
              color='neutral'
              disabled={deleteTask.state.isLoading}
            >
              <FormattedMessage defaultMessage='Cancel' />
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button
              variant='soft'
              color='danger'
              onClick={handleDelete}
              disabled={deleteTask.state.isLoading}
            >
              <FormattedMessage defaultMessage='Delete' />
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SubTaskDetails = ({
  subTaskIndex,
  subTaskUpdate,
  parentTaskForm,
}: {
  subTaskIndex: number;
  subTaskUpdate: UseFieldArrayUpdate<Task, 'children'>;
  parentTaskForm: UseFormReturn<Task>;
}) => {
  const subTaskData = parentTaskForm.watch(`children.${subTaskIndex}`);
  const taskForm = useForm({
    schema: taskSchema,
    values: subTaskData,
    mode: 'all',
  });
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();

  const remoteUpdateSubTask = useWrappedMutation(
    ProcessService.endpoints.updateTask.useMutation(),
  );

  const updateTask = async (
    data: AtLeastOne<Omit<SubTask, 'id' | 'parent'>>,
  ) => {
    if (!subTaskData) return;
    await remoteUpdateSubTask.mutateAsync(
      {
        id: subTaskData.id,
        ...data,
      },
      {
        onSuccess: (updatedTask) => {
          subTaskUpdate(subTaskIndex, updatedTask);
        },
        onError: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage:
                'An error occurred while processing your request. Please try again later.',
            }),
            'error',
          );
        },
      },
    );
  };

  return (
    <div className='px-4 pb-4 gap-4 flex flex-row'>
      <div className='bg-white p-6 rounded-md border border-gray-300 flex-1 gap-2'>
        <Controller
          control={taskForm.control}
          name='name'
          render={({ field, fieldState }) => (
            <div className='mb-4'>
              <Input
                variant='plain'
                placeholder='Task Name'
                sx={{
                  fontSize: 18,
                  border: '1px solid transparent',
                  borderRadius: 2,
                  '&:hover': {
                    background: '#F6F6F6',
                  },
                  '&.Mui-focused': {
                    paddingX: 2,
                  },
                }}
                {...field}
                onBlur={() => {
                  field.onBlur();
                  updateTask({ name: field.value });
                }}
              />
              {fieldState.error?.message && (
                <Typography color='danger' level='body-xs'>
                  {fieldState.error.message}
                </Typography>
              )}
            </div>
          )}
        />
        <Controller
          control={taskForm.control}
          name='description'
          render={({ field, fieldState }) => (
            <>
              <Typography fontWeight={500}>
                <FormattedMessage defaultMessage='Description' />
              </Typography>
              <TextArea
                placeholder='Add description...'
                {...field}
                onBlur={() => {
                  field.onBlur();
                  updateTask({ description: field.value });
                }}
                sx={{
                  border: '1px solid transparent',
                  borderRadius: 2,
                  '&:hover': {
                    background: '#F6F6F6',
                  },
                  '&.Mui-focused': {
                    border: '1px solid black',
                    padding: 2,
                  },
                }}
                minRows={10}
              />
              {fieldState.error?.message && (
                <Typography color='danger' level='body-xs'>
                  {fieldState.error.message}
                </Typography>
              )}
            </>
          )}
        />
      </div>
      <div className='basis-80 bg-white p-4 rounded-md border border-gray-300 space-y-2'>
        <Typography level='body-md' fontWeight={500}>
          <FormattedMessage defaultMessage='Details' />
        </Typography>
        <Typography level='body-md'>
          <FormattedMessage defaultMessage='Task Status' />
        </Typography>
        <div>
          <Chip>
            <FormattedMessage defaultMessage='TO DO' />
          </Chip>
        </div>
      </div>
    </div>
  );
};

export const SubtaskCount = ({ count }: { count: number | undefined }) => {
  if (!count || count < 1) return <></>;

  return (
    <div className='flex flex-row gap-1'>
      <Typography>{count}</Typography>
      <TreeView />
    </div>
  );
};

export const TaskDialog = ({
  index,
  update,
  remove,
}: {
  index: number;
  update: UseFieldArrayUpdate<TemplateSchema, 'subtasks'>;
  remove: UseFieldArrayRemove;
}) => {
  const [showSubtaskField, setShowSubtaskField] = useState(false);
  const [activeSubTaskIndex, setActiveSubTaskIndex] = useState<number | null>(
    null,
  );
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const templateForm = useFormContext<TemplateSchema>();
  const taskData =
    index !== undefined ? templateForm.watch(`subtasks.${index}`) : undefined;

  const templateID = templateForm.watch('id');
  const taskForm = useForm({
    schema: taskWithChildrenSchema,
    values: taskData,
  });

  const createSubTask = useWrappedMutation(
    ProcessService.endpoints.createSubTask.useMutation(),
  );
  const remoteUpdateTask = useWrappedMutation(
    ProcessService.endpoints.updateTask.useMutation(),
  );

  const activeSubTask =
    activeSubTaskIndex !== null
      ? taskForm.watch(`children.${activeSubTaskIndex}`)
      : null;

  const subTaskForm = useForm({
    schema: newSubTaskSchema,
    defaultValues: {
      name: '',
      description: '',
      parent: taskData ? taskData.id : null,
      template: templateID,
    },
  });

  const {
    fields,
    append,
    update: subTaskUpdate,
    remove: subTaskRemove,
  } = useFieldArray({
    control: taskForm.control,
    name: 'children',
  });

  const closeDialog = () => {
    if (index === undefined) return;
    taskForm.handleSubmit((data) => {
      update(index, data);
    })();
  };

  const openSubtaskField = () => {
    setShowSubtaskField(true);
  };

  const closeSubtaskField = () => {
    setShowSubtaskField(false);
    subTaskForm.reset();
  };

  const addSubtask = () => {
    subTaskForm.handleSubmit(async (data) => {
      await createSubTask.mutateAsync(data, {
        onSuccess: (newSubtask) => {
          subTaskForm.reset();
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Subtask added.' }),
            'success',
          );
          append(newSubtask);
          closeSubtaskField();
        },
        onError: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage:
                'An error occurred while processing your request. Please try again later.',
            }),
            'error',
          );
        },
      });
    })();
  };

  const updateTask = async (data: AtLeastOne<Omit<SubTask, 'id'>>) => {
    if (!taskData) return;
    await remoteUpdateTask.mutateAsync(
      {
        id: taskData.id,
        ...data,
      },
      {
        onError: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage:
                'An error occurred while processing your request. Please try again later.',
            }),
            'error',
          );
        },
      },
    );
  };

  const handleDeleteClicked = () => {
    if (activeSubTaskIndex !== null) {
      setActiveSubTaskIndex(null);
      subTaskRemove(activeSubTaskIndex);
    }
    if (activeSubTaskIndex === null && index !== null) {
      remove(index);
    }
  };

  const handleCloseDialog = (open: boolean) => {
    if (open) return;
    if (index === null) return;
    taskForm.handleSubmit((data) => {
      update(index, data);
    })();
  };

  return (
    <Dialog onOpenChange={handleCloseDialog}>
      <DialogTrigger asChild>
        <Button
          variant='plain'
          color='neutral'
          endDecorator={<SubtaskCount count={taskData?.children?.length} />}
        >
          {taskData?.name}
        </Button>
      </DialogTrigger>
      {taskData && (
        <DialogContent size='5xl'>
          <div className='flex justify-between items-center p-4'>
            <Breadcrumbs
              sx={{
                borderRadius: 1,
                color: 'gray',
                border: '1px solid #D1D1D1',
                background: 'white',
                padding: 1,
              }}
            >
              <Button
                variant='plain'
                color='neutral'
                onClick={() => setActiveSubTaskIndex(null)}
              >
                {taskData.control_id}
              </Button>
              {activeSubTaskIndex !== null && (
                <Button variant='plain' color='neutral'>
                  {activeSubTask?.control_id}
                </Button>
              )}
            </Breadcrumbs>
            <div className='flex gap-2'>
              <DeleteWarningDialog
                taskID={activeSubTask ? activeSubTask.id : taskData.id}
                onSuccessfulDelete={handleDeleteClicked}
              />
              <IconButton onClick={closeDialog} size='lg'>
                <X size={20} />
              </IconButton>
            </div>
          </div>
          {activeSubTaskIndex !== null && index !== null ? (
            <SubTaskDetails
              subTaskIndex={activeSubTaskIndex}
              subTaskUpdate={subTaskUpdate}
              parentTaskForm={taskForm}
            />
          ) : (
            <div className='px-4 pb-4 gap-2 flex'>
              <div className='bg-white p-6 rounded-md border border-gray-300 flex-1 gap-2'>
                <Controller
                  name='name'
                  control={taskForm.control}
                  render={({ field, fieldState }) => (
                    <div className='mb-4'>
                      <Input
                        variant='plain'
                        placeholder='Task Name'
                        sx={{
                          border: '1px solid transparent',
                          '&:hover': {
                            background: '#F6F6F6',
                          },
                          '&.Mui-focused': {
                            paddingX: 2,
                          },
                          borderRadius: 2,
                          fontSize: 18,
                        }}
                        {...field}
                        onBlur={() => {
                          field.onBlur();
                          updateTask({ name: field.value });
                        }}
                      />
                      {fieldState.error?.message && (
                        <Typography color='danger' level='body-xs'>
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </div>
                  )}
                />
                <Controller
                  control={taskForm.control}
                  name='description'
                  render={({ field, fieldState }) => (
                    <>
                      <Typography fontWeight={500}>
                        <FormattedMessage defaultMessage='Description' />
                      </Typography>
                      <TextArea
                        {...field}
                        onBlur={() => {
                          field.onBlur();
                          updateTask({ description: field.value });
                        }}
                        sx={{
                          border: '1px solid transparent',
                          '&:hover': {
                            background: '#F6F6F6',
                          },
                          '&.Mui-focused': {
                            padding: 2,
                            border: '1px solid black',
                          },
                          borderRadius: 2,
                        }}
                        minRows={10}
                        placeholder='Add description...'
                      />
                      {fieldState.error?.message && (
                        <Typography color='danger' level='body-xs'>
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
                <div>
                  <Typography fontWeight={500} paddingY={2}>
                    <FormattedMessage defaultMessage='Subtasks' />
                  </Typography>
                  <div className='max-h-96'>
                    {fields.map((field, fieldIndex) => (
                      <Button
                        key={field.name}
                        sx={{
                          paddingX: 2,
                          width: '100%',
                          borderTop: '1px solid #D1D1D1',
                          borderRadius: 0,
                          borderBottom:
                            fieldIndex === fields.length - 1
                              ? '1px solid #D1D1D1'
                              : '',
                          gap: 2,
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          paddingY: 1,
                        }}
                        variant='plain'
                        onClick={() => setActiveSubTaskIndex(fieldIndex)}
                        startDecorator={
                          <Typography color='neutral' fontWeight={500}>
                            {field.control_id}
                          </Typography>
                        }
                      >
                        <Typography>{field.name}</Typography>
                      </Button>
                    ))}
                  </div>
                  <div className='py-4'>
                    {!showSubtaskField && (
                      <Button
                        variant='plain'
                        color='neutral'
                        sx={{
                          textTransform: 'none',
                        }}
                        startDecorator={<Plus />}
                        type='button'
                        onClick={openSubtaskField}
                      >
                        <FormattedMessage defaultMessage='Add subtask' />
                      </Button>
                    )}
                    {showSubtaskField && (
                      <div className='px-6 py-4 space-y-4 rounded-md border border-black'>
                        <InputField
                          control={subTaskForm.control}
                          field='name'
                          placeholder='Subtask Name'
                          variant='outlined'
                        />
                        <TextAreaField
                          control={subTaskForm.control}
                          field='description'
                          placeholder='Subtask Description'
                          variant='outlined'
                          minRows={4}
                        />
                        <div className='flex gap-2'>
                          <StatusChip status={TaskStatus.TODO} />
                          <SelectField
                            control={subTaskForm.control}
                            field='priority'
                            mountOptionsToRoot={true}
                          >
                            <PrioritySelectChoices />
                          </SelectField>
                        </div>
                        <div className='flex justify-end gap-2'>
                          <Button
                            variant='plain'
                            color='neutral'
                            sx={{
                              textTransform: 'none',
                            }}
                            onClick={closeSubtaskField}
                            disabled={createSubTask.state.isLoading}
                          >
                            <FormattedMessage defaultMessage='Cancel' />
                          </Button>
                          <Button
                            variant='solid'
                            color='secondary'
                            sx={{
                              textTransform: 'none',
                            }}
                            onClick={addSubtask}
                            disabled={createSubTask.state.isLoading}
                          >
                            <FormattedMessage defaultMessage='Create subtask' />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className='basis-80 bg-white p-4 rounded-md border border-gray-300 space-y-3'>
                <Typography level='body-md' fontWeight={500}>
                  <FormattedMessage defaultMessage='Details' />
                </Typography>
                <Typography level='body-md'>
                  <FormattedMessage defaultMessage='Task Status' />
                </Typography>
                <div>
                  <StatusChip status={TaskStatus.TODO} />
                </div>
                <Typography level='body-md'>
                  <FormattedMessage defaultMessage='Priority' />
                </Typography>
                <SelectField
                  control={taskForm.control}
                  field='priority'
                  mountOptionsToRoot={true}
                >
                  <PrioritySelectChoices />
                </SelectField>
              </div>
            </div>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export async function loader({ params }: LoaderFunctionArgs) {
  const templateID = params.id;

  if (!templateID) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw new Response('Template ID not found', {
      status: 404,
      statusText: 'ERR_NO_TEMPLATE_ID',
    });
  }

  const query = store.dispatch(
    ProcessService.endpoints.processTemplate.initiate({ id: templateID }),
  );

  const templateData = await query;
  query.unsubscribe();

  if (templateData.isSuccess) {
    return json(templateData.data);
  }
  // eslint-disable-next-line @typescript-eslint/no-throw-literal
  throw new Response('Template not found', {
    status: 404,
    statusText: 'ERR_NO_TEMPLATE',
  });
}

export function Component() {
  const navigate = useNavigate();
  const data = useLoaderData() as CreateUpdateTemplate;
  const form = useForm({
    schema: formSchema,
    mode: 'all',
    values: data,
  });
  const updateProcessTemplate = useWrappedMutation(
    ProcessService.endpoints.updateProcessTemplate.useMutation(),
  );

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: 'subtasks',
  });
  const intl = useIntl();

  const { Table, Column } = useTableBuilder<Task>();

  const processName = form.watch('name');

  const templateID = form.watch('id');

  const handleUpdate = async (updateData: Pick<TemplateSchema, 'name'>) => {
    await updateProcessTemplate.mutateAsync({
      name: updateData.name,
      id: templateID,
    });
  };

  const closeTemplate = () => {
    navigate('/customer/settings/templates');
  };

  return (
    <FormProvider {...form}>
      <div className='flex flex-col w-full rounded-lg border border-gray-300 h-full bg-white'>
        <div className='flex justify-between items-center border-b border-b-gray-300 p-4'>
          <Typography>{processName}</Typography>
          <div className='flex justify-end gap-4'>
            <DeleteTemplateWarningDialog
              templateID={templateID}
              onSuccessfulDelete={() => closeTemplate()}
            />
            <Button variant='plain' color='neutral' onClick={closeTemplate}>
              <FormattedMessage defaultMessage='Close' />
            </Button>
          </div>
        </div>
        <div className='flex flex-col p-6 flex-grow gap-4'>
          <div className='flex flex-col justify-center gap-4'>
            <div className='flex justify-between items-center'>
              <Controller
                control={form.control}
                name='name'
                render={({ field }) => (
                  <Input
                    placeholder='Template name...'
                    sx={{
                      fontSize: 20,
                    }}
                    variant='plain'
                    {...field}
                    onBlur={() => {
                      field.onBlur();
                      handleUpdate({ name: field.value });
                    }}
                  />
                )}
              />
              {fields.length > 0 && <NewTaskDialog onCreate={append} />}
            </div>
            {fields.length === 0 && (
              <Stack justifyContent='center' gap={1}>
                <Typography textAlign='center' level='title-lg'>
                  <FormattedMessage defaultMessage='Add new task to the process' />
                </Typography>
                <Typography textAlign='center' level='body-md' color='neutral'>
                  <FormattedMessage defaultMessage='Quickly add new tasks to keep the process moving' />
                </Typography>
                <Stack flexDirection='row' justifyContent='center' py={2}>
                  <NewTaskDialog onCreate={append} />
                </Stack>
              </Stack>
            )}
            {fields.length > 0 && (
              <Table data={fields} hoverRow>
                <Column
                  header={intl.formatMessage({ defaultMessage: 'Control ID' })}
                >
                  {(row) => row.control_id}
                </Column>
                <Column
                  style={{
                    width: '30%',
                  }}
                  header={intl.formatMessage({ defaultMessage: 'Task Name' })}
                >
                  {(row) => {
                    const index = fields.findIndex(
                      (field) => field.control_id === row.control_id,
                    );
                    return (
                      <TaskDialog
                        index={index}
                        update={update}
                        remove={remove}
                      />
                    );
                  }}
                </Column>
                <Column
                  header={intl.formatMessage({ defaultMessage: 'Assignee' })}
                >
                  {() => <></>}
                </Column>
                <Column
                  header={intl.formatMessage({ defaultMessage: 'Due Date' })}
                >
                  {() => <></>}
                </Column>
                <Column
                  header={intl.formatMessage({ defaultMessage: 'Priority' })}
                >
                  {(row) => (
                    <PriorityChip priority={getPriorityName(row.priority)} />
                  )}
                </Column>
                <Column
                  header={intl.formatMessage({ defaultMessage: 'Task Status' })}
                >
                  {() => <StatusChip status={TaskStatus.TODO} />}
                </Column>
              </Table>
            )}
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
